import { useState, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import {
  OverrideType,
  PhoneOverride,
  CallRouteType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import dayjs from 'dayjs';
import { CallRouteV1 } from '@frontend/api-call-route';
import { ForwardingNumberApi } from '@frontend/api-forwarding-number';
import { LocationsApi } from '@frontend/api-locations';
import PhoneOverrideApi from '@frontend/api-overrides-v2';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { AnimatedBadge } from '@frontend/components';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { SurfaceHeader } from '@frontend/surface-header';
import { useEligibility } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import {
  useModalControl,
  UseFormReturnType,
  useForm,
  Tray,
  Heading,
  CheckboxField,
  DatePickerField,
  TimeField,
  Text,
  useAlert,
  TextLink,
  DropdownField,
  Button,
  RadioCardField,
  Modal,
  Truncated,
  InlineNotification,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { useOverridesManagementPermission } from '../../userManagementPermission';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { defaultMediaID, getNameWithoutExtension } from '../../utils/phone-utils';
import { AudioQueryMedia, useAudioQuery } from '../../utils/use-audio-query';
import { newOverrideButtonSectionStyle, overrideSettingsStyle } from '../../views/settings/overrides-styles';
import { AudioPicker, DEFAULT_GREETING_PATH, findMediaItem } from '../audio-picker/audio-picker';
import { getCanvasPhoneTrees } from '../call-routes/helpers/graph/node-utils';
import { logError } from '../call-routes/helpers/utils';
import { CachedAudioScrubber } from '../common/cached-audio-scrubber';
import { PhoneTreeDropdown } from '../common/phone-tree-dropdown/phone-tree-dropdown';
import { PhoneTreePreviewModal } from '../common/phone-tree-dropdown/phone-tree-preview-modal';
import { getMediaValues } from '../department/phone-routing-settings';
import { ForwardingNumberPicker } from '../forwarding-number-picker';
import { TraySubHeader } from '../tray-sub-header';
import useVoicemailBoxes from '../voicemail-box/new/use-voicemail-boxes';
import { NewOverrideSettingRemoveModal } from './override-settings-remove-modal.new';
import { OverrideCreatePayload, OverrideUpdatePayload, PhoneOverrideType, SchemaFormValues } from './types';
import { useOverrideMutations } from './use-overrides-mutations';
import { VoicemailIvrNotification } from './voicemail-ivr-notification';

const formConfigBase = {
  voicemailBoxId: { type: 'dropdown' },
  voicemailMediaId: { type: 'dropdown' },
  phoneTreeId: { type: 'dropdown' },
  phoneTreeInfoMediaId: { type: 'dropdown' },
  forwardingNumberId: { type: 'dropdown' },
  selectOverride: { type: 'radio' },
  addSchedule: { type: 'checkbox' },
  startDate: { type: 'datePicker' },
  startTime: { type: 'time' },
  endDate: { type: 'datePicker' },
  endTime: { type: 'time' },
} as const;

interface IOverrideSettingTray {
  mountTarget?: string;
  override?: PhoneOverrideType | PhoneOverride;
  locationId?: string;
  modalControls: ReturnType<typeof useModalControl>;
  showUnsavedPhoneTreeOptions?: boolean;
  isLoading?: boolean;
  /** Callback to save the call route */
  onSaveCallRoute?: () => Promise<void>;
  callRouteNodes?: CallRouteV1.Types.Node[];
  callRouteEdges?: Edge[];
}

// We need to calculate the time difference between userTime and officeTime. This difference will be applied to the payload to ensure that the time in the timeField aligns with the office timezone, ensuring consistency between the selected time and the expected response.
const getTimezoneDifference = (officeTimezone: string | undefined) => {
  const userTime = new Date(dayjs().format('MM/DD/YY HH:mm'));
  const officeTime = new Date(dayjs().tz(officeTimezone).format('MM/DD/YY HH:mm'));
  return (officeTime.getTime() - userTime.getTime()) / (1000 * 60 * 60);
};

const timeToSeconds = (timeStr: string) => {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const NewOverrideSettingTray = ({
  mountTarget,
  override,
  locationId: iLID,
  modalControls,
  showUnsavedPhoneTreeOptions = false,
  onSaveCallRoute,
  isLoading = false,
  callRouteNodes = [],
  callRouteEdges = [],
}: IOverrideSettingTray) => {
  return (
    <Tray
      mountTarget={mountTarget}
      width='xlarge'
      css={{ padding: theme.spacing(4), borderTop: `1px solid ${theme.colors.neutral20}`, overflowY: 'auto' }}
      {...modalControls.modalProps}
    >
      <OverrideSettingTrayContent
        useExtraSpacing={!!mountTarget}
        override={override}
        locationId={iLID}
        showUnsavedPhoneTreeOptions={showUnsavedPhoneTreeOptions}
        onClose={modalControls.closeModal}
        onSaveCallRoute={onSaveCallRoute}
        isLoading={isLoading}
        callRouteNodes={callRouteNodes}
        callRouteEdges={callRouteEdges}
      />
    </Tray>
  );
};

const OverrideSettingTrayContent = ({
  useExtraSpacing,
  override,
  locationId: iLID,
  onClose,
  onSaveCallRoute,
  showUnsavedPhoneTreeOptions = false,
  isLoading = false,
  callRouteNodes = [],
  callRouteEdges = [],
}: {
  useExtraSpacing?: boolean;
  override?: PhoneOverrideType | PhoneOverride;
  locationId?: string;
  showUnsavedPhoneTreeOptions?: boolean;
  isLoading?: boolean;
  callRouteNodes?: CallRouteV1.Types.Node[];
  callRouteEdges?: Edge[];
  onClose?: () => void;
  onSaveCallRoute?: () => Promise<void>;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const alerts = useAlert();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const locationData = accessibleLocationData[selectedLocationIds[0]];
  const phoneTenantId = locationData?.phoneTenantId ?? '';
  const instructionSetId = override?.instructionSetId ?? '';
  const { data: audioFiles } = useAudioQuery({
    tenantId: phoneTenantId,
    allowedOptions: { standard: true, custom: true, mailbox: true },
    mailboxId: override?.voicemailBoxId,
  });

  const { getFeatureFlagValue } = useAppFlagStore();
  const hasPhoneTreeOverridesFlag = getFeatureFlagValue({
    flagName: 'pc-phone-tree-overrides',
    locationIds: selectedLocationIds,
  });

  const isDeviceRouteOverride = override?.callRouteType === CallRouteType_Enum.DEVICE_EXTENSION;
  const callRouteId = override?.callRouteId;

  const { data: overridesData = { phoneOverrides: [], lastUsed: {}, deviceExtensionOverrides: [] } } = useQuery({
    queryKey: [
      phoneTenantId,
      ...queryKeys.settings.listOverrides({
        includeDeviceExtensionOverrides: isDeviceRouteOverride,
      }),
    ],
    queryFn: async () =>
      PhoneOverrideApi.List({
        tenantId: phoneTenantId,
        includes: { deviceExtensionOverrides: isDeviceRouteOverride },
      }),
    enabled: !!phoneTenantId,
    retry: false,
  });

  const getMediaNameById = (id: string) => {
    const media = audioFiles?.allMedia.find((item) => item.id === id);
    return media ? getNameWithoutExtension(media.name) : null;
  };

  const { data: forwardingNumbers } = useQuery({
    queryKey: [override?.locationId, ...queryKeys.forwardingNumbers()],
    queryFn: () => ForwardingNumberApi.list({ locationId: override?.locationId }),
    enabled: !!override,
  });
  const forwardingNumber = forwardingNumbers?.find((item) => item.id === override?.forwardingNumberId);

  const lastUsedVMO = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_VMO];
  const lastUsedFWD = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_FWD];
  const lastUsedIVR = overridesData?.lastUsed?.[instructionSetId ?? '']?.lastUsed?.[OverrideType.OVERRIDE_TYPE_IVR];
  const lastUsedMediaItemId = override?.mediaItemId ? '' : lastUsedVMO?.mediaItemId;
  const lastUsedForwardingNumberId = override?.forwardingNumberId ? '' : lastUsedFWD?.forwardingNumberId;
  const lastUsedVoicemailBoxId = override?.voicemailBoxId ? '' : lastUsedVMO?.voicemailBoxId;
  const lastUsedIVRId = override?.ivrMenuId ? '' : lastUsedIVR?.ivrMenuId;
  const lastUsedIVRMediaId = override?.mediaItemId ? '' : lastUsedIVR?.mediaItemId;

  // If voicemailIvr is set, it means that this override is being using as a Voicemail Override backed by the skip
  // schedule IVR in PDS. Users need notified that they need to use the new phone tree override instead.
  const voicemailIvr = override?.voicemailIvr;

  // Fetch call route data
  const { data: callRoute } = CallRouteV1.Queries.useReadQuery({
    request: { callRouteId: callRouteId ?? '' },
    options: {
      enabled: !!callRouteId && (!callRouteNodes.length || !callRouteEdges.length),
      onError: (error) => {
        alerts.error(t('Failed to load call route.'));
        logError('Failed to fetch call route in override settings', 'useCallRouteData:useReadQuery', {
          error,
          callRouteId,
          phoneTenantId,
        });
      },
    },
  });
  const nodes = callRouteNodes.length ? callRouteNodes : ((callRoute?.nodes || []) as CallRouteV1.Types.Node[]);
  const edges = callRouteEdges.length ? callRouteEdges : callRoute?.edges || [];

  const phoneTreesInCallRoute = callRouteId && nodes.length && edges.length ? getCanvasPhoneTrees(nodes, edges) : [];

  // Filter out any phone trees that don't have an primitiveId which means those are not trees
  // that have been saved and exist only as unsaved nodes in the call route, as those are not valid
  // options for overrides
  const filteredPhoneTreesInCallRoute = showUnsavedPhoneTreeOptions
    ? phoneTreesInCallRoute
    : phoneTreesInCallRoute?.filter((tree) => !!tree.callObject?.primitiveId);

  const hasUnsavedPhoneTrees = filteredPhoneTreesInCallRoute.some((tree) => {
    return !tree.callObject?.primitiveId;
  });

  const getVoicemailBoxNameById = (id: string) => {
    const voicemailBox = voicemailBoxes?.find((item) => item.mailbox.id === id);
    return voicemailBox ? voicemailBox.mailbox.name : null;
  };

  const hasUserManagementPermission = useOverridesManagementPermission();
  const removeOverride = useModalControl();
  const { modalProps: previewPhoneTreeModalProps, triggerProps: previewPhoneTreeTriggerProps } = useModalControl();
  const locationId = iLID ?? override?.locationId ?? '';

  const { globalAvailableLocationIds } = usePhoneSettingsShallowStore('globalAvailableLocationIds');

  const { data: voicemailBoxes = [] } = useVoicemailBoxes({
    phoneTenantId: locationData.phoneTenantId ?? '',
  });

  const filteredVoicemailBoxes = useMemo(
    () =>
      scopedFilterAndSort({
        data: voicemailBoxes,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: [],
        shouldSort: false,
      }),
    [voicemailBoxes, globalAvailableLocationIds]
  );

  const voicemailBox = filteredVoicemailBoxes.filter((vm) => vm.mailbox.id === override?.voicemailBoxId);

  const { data: officeTimezone } = useQuery({
    queryKey: [locationId, ...queryKeys.timezone(locationId ?? '')],
    queryFn: () =>
      LocationsApi.getOfficeTimezone(
        {},
        {
          locationId,
        }
      ),
    select: (data) => data.timezone,
  });

  const currentDate = dayjs();
  const currentDay = dayjs().startOf('day');
  const currentDateFormatted = dayjs().format('MM/DD/YYYY');
  const roundedTime = currentDate.minute(Math.ceil(currentDate.minute() / 15) * 15);

  /// we know that if there is a matching override, then the override is being edited
  const isEdit = !!override?.id;

  const timezoneDiff = getTimezoneDifference(officeTimezone);
  const currentTimeOffice = roundedTime.add(timezoneDiff, 'hour').format('HH:mm');

  const createPostPayloadV2 = (
    values: UseFormReturnType<typeof formConfigBase, keyof typeof formConfigBase>['values'] & { id?: string },
    instructionSetId: string
  ): OverrideCreatePayload => {
    const formattedStartDateTime = dayjs(`${values.startDate} ${values.startTime}`)
      .subtract(timezoneDiff, 'hour')
      .format();
    const formattedEndDateTime = dayjs(`${values.endDate} ${values.endTime}`).subtract(timezoneDiff, 'hour').format();

    const baseObj: OverrideCreatePayload = {
      instructionSetId,
      overrideType: values.selectOverride as OverrideType,
    };

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailBoxId) {
      baseObj.voicemailBoxId = values.voicemailBoxId;
    } else baseObj.voicemailBoxId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailMediaId !== '') {
      baseObj.mediaItemId = values.voicemailMediaId && getMediaValues(values.voicemailMediaId);
    } else baseObj.mediaItemId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_FWD && values.forwardingNumberId !== '') {
      baseObj.forwardingNumberId = values.forwardingNumberId;
    } else baseObj.forwardingNumberId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR && values.phoneTreeId !== '') {
      baseObj.ivrMenuId = values.phoneTreeId;
    } else baseObj.ivrMenuId = '';

    if (
      baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR &&
      values.phoneTreeInfoMediaId !== '' &&
      values.phoneTreeInfoMediaId !== 'none'
    ) {
      baseObj.mediaItemId = values.phoneTreeInfoMediaId;
    }

    if (values.addSchedule && values.startDate !== '' && values.startTime !== '') {
      baseObj.startAt = formattedStartDateTime;
    }

    if (values.addSchedule && values.endDate !== '' && values.endTime !== '') {
      baseObj.endAt = formattedEndDateTime;
    }

    return baseObj;
  };

  const updatePostPayloadV2 = (
    values: UseFormReturnType<typeof formConfigBase, keyof typeof formConfigBase>['values'] & { id?: string },
    instructionSetId: string
  ): OverrideUpdatePayload => {
    const formattedStartDateTime = dayjs(`${values.startDate} ${values.startTime}`)
      .subtract(timezoneDiff, 'hour')
      .format();
    const formattedEndDateTime = dayjs(`${values.endDate} ${values.endTime}`).subtract(timezoneDiff, 'hour').format();

    const baseObj: OverrideUpdatePayload = {
      instructionSetId,
      id: values.id || '',
      overrideType: values.selectOverride as OverrideType,
    };

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailBoxId) {
      baseObj.voicemailBoxId = values.voicemailBoxId;
    } else baseObj.voicemailBoxId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_VMO && values.voicemailMediaId) {
      baseObj.mediaItemId = getMediaValues(values.voicemailMediaId);
    } else baseObj.mediaItemId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_FWD && values.forwardingNumberId) {
      baseObj.forwardingNumberId = values.forwardingNumberId;
    } else baseObj.forwardingNumberId = '';

    if (baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR && values.phoneTreeId) {
      baseObj.ivrMenuId = values.phoneTreeId;
    } else baseObj.ivrMenuId = '';

    if (
      baseObj.overrideType === OverrideType.OVERRIDE_TYPE_IVR &&
      values.phoneTreeInfoMediaId &&
      values.phoneTreeInfoMediaId !== 'none'
    ) {
      baseObj.mediaItemId = values.phoneTreeInfoMediaId;
    }

    if (optionalFieldProps.value && values.addSchedule && values.startDate && values.startTime) {
      baseObj.startAt = formattedStartDateTime;
    }

    if (optionalFieldProps.value && values.addSchedule && values.endDate && values.endTime) {
      baseObj.endAt = formattedEndDateTime;
    }

    if (override?.id) {
      baseObj.id = override.id;
    }

    return baseObj;
  };

  const { formProps, getFieldProps, seedValues, validate } = useForm({
    computeChangedValues: true,
    fields: {
      voicemailBoxId: {
        type: 'dropdown',
        value: override?.voicemailBoxId,
      },
      voicemailMediaId: {
        type: 'dropdown',
        value:
          override?.overrideType === OverrideType.OVERRIDE_TYPE_VMO && override?.mediaItemId
            ? override?.mediaItemId
            : '',
      },
      phoneTreeId: { type: 'dropdown', value: override?.ivrMenuId },
      phoneTreeInfoMediaId: {
        type: 'dropdown',
        value:
          override?.overrideType === OverrideType.OVERRIDE_TYPE_IVR && override?.mediaItemId
            ? override?.mediaItemId
            : '',
      },
      forwardingNumberId: { type: 'dropdown' },
      selectOverride: { type: 'radio' },
      addSchedule: { type: 'checkbox' },
      startDate: {
        type: 'datePicker',
        validator: ({ value }, formValues) => {
          const startDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          // Skip start date validation for an active override (indicated by override?.enabled) if the start date and time have passed. This specifically applies when editing an override.
          if (formValues.endDate.value === '' && !override?.enabled) {
            return startDate.isBefore(currentDate, 'day') ? t('Start date must be in the future') : '';
          }

          return startDate.isAfter(endDate) ? t('Start date must be before the end date') : '';
        },
      },
      startTime: {
        type: 'time',
        validator: ({ value }, formValues) => {
          const startTime = timeToSeconds(value);
          const endTime = timeToSeconds(formValues.endTime.value);
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          const currentTime = timeToSeconds(dayjs().add(timezoneDiff, 'hour').format('HH:mm:ss'));
          // Skip start time validation for an active override (indicated by override?.enabled) if the start date and time have passed. This specifically applies when editing an override.
          if (startDate.isSame(currentDate, 'day') && !startDate.isSame(endDate) && !override?.enabled) {
            return startTime < currentTime ? t('Start time must be in the future') : '';
          }

          return startDate.isSame(endDate) && startTime >= endTime ? t('Start time must be before the end time') : '';
        },
      },
      endDate: {
        type: 'datePicker',
        validator: ({ value }, formValues) => {
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(value, 'MM/DD/YYYY').startOf('day');

          if (!formValues.startDate.value) {
            return endDate.isBefore(currentDay) ? t('End date must be in the future') : '';
          }

          return endDate.isBefore(startDate) ? t('End date must be after the start date') : '';
        },
      },
      endTime: {
        type: 'time',
        validator: ({ value }, formValues) => {
          const startTime = timeToSeconds(formValues.startTime.value);
          const endTime = timeToSeconds(value);
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');
          const currentTime = timeToSeconds(dayjs().add(timezoneDiff, 'hour').format('HH:mm:ss'));

          if (endDate.isSame(currentDate, 'day') && !startDate.isSame(endDate)) {
            return endTime < currentTime ? t('End time must be in the future') : '';
          }
          return startDate.isSame(endDate) && startTime >= endTime ? t('End time must be after the start time') : '';
        },
      },
    },
    onSubmit: (formValues) => {
      let instructionSetId = '';
      if (override?.instructionSetId) {
        instructionSetId = override.instructionSetId;
      } else if (override && 'instructionSetId' in override) {
        instructionSetId = override.instructionSetId;
      }
      if (override?.id) {
        return updateOverrideV2(formValues, instructionSetId);
      }
      return createOverrideV2(formValues, instructionSetId);
    },
  });

  const voicemailBoxProps = getFieldProps('voicemailBoxId');
  const vmOverrideMediaProps = getFieldProps('voicemailMediaId');
  const phoneTreeIdProps = getFieldProps('phoneTreeId');
  const phoneTreeInfoMediaIdProps = getFieldProps('phoneTreeInfoMediaId');
  const callForwardingNumberProps = getFieldProps('forwardingNumberId');
  const overrideSelectProps = getFieldProps('selectOverride');
  const optionalFieldProps = getFieldProps('addSchedule');
  const startDateFieldProps = getFieldProps('startDate');
  const startTimeFieldProps = getFieldProps('startTime');
  const endDateFieldProps = getFieldProps('endDate');
  const endTimeFieldProps = getFieldProps('endTime');
  const startDate = startDateFieldProps?.value;
  const endDate = endDateFieldProps?.value;
  const isStartToday = dayjs(startDate).isSame(currentDate, 'day');
  const isEndToday = dayjs(endDate).isSame(currentDate, 'day');

  const { data: phoneTreeData } = useQuery({
    queryKey: [locationData.phoneTenantId, ...queryKeys.settings.readPhoneTree(phoneTreeIdProps?.value ?? '')],
    queryFn: () => PhoneTreeAPI.Read({ phoneTreeId: phoneTreeIdProps?.value ?? '' }),
    enabled: !!locationData.phoneTenantId && !!phoneTreeIdProps?.value,
  });

  useEffect(() => {
    validate();
  }, [startDateFieldProps.value, endDateFieldProps.value, startTimeFieldProps.value, endTimeFieldProps.value]);

  useEffect(() => {
    if (!!override && timezoneDiff !== undefined) {
      seedValues({
        selectOverride:
          override.overrideType !== OverrideType.OVERRIDE_TYPE_UNSPECIFIED
            ? override.overrideType
            : OverrideType.OVERRIDE_TYPE_VMO,
        voicemailBoxId: override.voicemailBoxId || '',
        voicemailMediaId:
          override.overrideType === OverrideType.OVERRIDE_TYPE_VMO
            ? override.mediaItemId || (override.id ? 'default' : '')
            : '',
        phoneTreeId: override.ivrMenuId || '',
        phoneTreeInfoMediaId:
          override.overrideType === OverrideType.OVERRIDE_TYPE_IVR ? override.mediaItemId || 'none' : '',
        forwardingNumberId: override.forwardingNumberId,
        addSchedule: !!override.startAt || !!override.endAt,
        startDate: !!override.startAt ? dayjs(override.startAt).format('MM/DD/YYYY') : '',
        startTime: !!override.startAt ? dayjs(override.startAt).add(timezoneDiff, 'hour').format('h:mma') : '',
        endDate: !!override.endAt ? dayjs(override.endAt).format('MM/DD/YYYY') : '',
        endTime: !!override.endAt ? dayjs(override.endAt).add(timezoneDiff, 'hour').format('h:mma') : '',
      });
    } else
      seedValues({
        selectOverride: OverrideType.OVERRIDE_TYPE_VMO,
        voicemailBoxId: '',
        voicemailMediaId: '',
        forwardingNumberId: '',
        addSchedule: false,
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
      });
  }, [override, timezoneDiff, filteredVoicemailBoxes]);

  const hasValidationError =
    !!startDateFieldProps.error.length ||
    !!startTimeFieldProps.error.length ||
    !!endDateFieldProps.error.length ||
    !!endTimeFieldProps.error.length;

  const canUpdateOverride =
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO &&
      !hasValidationError &&
      !!voicemailBoxProps.value &&
      !!vmOverrideMediaProps.value) ||
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_FWD &&
      !hasValidationError &&
      !!callForwardingNumberProps.value) ||
    (overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_IVR &&
      !hasValidationError &&
      hasPhoneTreeOverridesFlag &&
      !!phoneTreeIdProps.value);

  const needTimeField =
    !!optionalFieldProps.value &&
    [
      { start: startDateFieldProps, end: startTimeFieldProps },
      { start: endDateFieldProps, end: endTimeFieldProps },
    ].some(({ start, end }) => !!start.value !== !!end.value);

  const handleCancel = () => {
    isCancelling.current = true;
    const isOverrideActive = override && !!override.id;
    const isVMO = isOverrideActive && override?.overrideType === OverrideType.OVERRIDE_TYPE_VMO;
    const isIVR = isOverrideActive && override?.overrideType === OverrideType.OVERRIDE_TYPE_IVR;
    seedValues({
      selectOverride: isOverrideActive ? override.overrideType : OverrideType.OVERRIDE_TYPE_VMO,
      voicemailBoxId: isOverrideActive ? override.voicemailBoxId : '',
      voicemailMediaId: isOverrideActive
        ? isVMO
          ? override.mediaItemId || 'default'
          : getMediaValues(defaultMediaID)
        : '',
      forwardingNumberId: isOverrideActive ? override.forwardingNumberId : '',
      phoneTreeId: isOverrideActive ? override.ivrMenuId : '',
      phoneTreeInfoMediaId: isOverrideActive ? (isIVR ? override.mediaItemId || 'none' : '') : '',
      addSchedule: isOverrideActive ? !!override.startAt || !!override.endAt : false,
      startDate: isOverrideActive && override.startAt ? dayjs(override.startAt).format('MM/DD/YYYY') : '',
      startTime: isOverrideActive && override.startAt ? dayjs(override.startAt).format('h:mma') : '',
      endDate: isOverrideActive && override.endAt ? dayjs(override.endAt).format('MM/DD/YYYY') : '',
      endTime: isOverrideActive && override.endAt ? dayjs(override.endAt).format('h:mma') : '',
    });

    // Reset flag after a short delay to allow the update to complete
    setTimeout(() => {
      isCancelling.current = false;
    }, 500);
  };

  const handleSaveCallRoute = async () => {
    if (onSaveCallRoute) {
      try {
        await onSaveCallRoute();
      } catch (error) {
        alerts.error(t('Failed to save call route. Please try again.'));
      }
    }
  };

  const isCancelling = useRef(false);
  const previousVoicemailBoxId = useRef(voicemailBoxProps.value);

  const { disableEligibility: disableNotification } = useEligibility({
    featureId: 'pc-phone-hours-phone-tree-overrides',
    // useEligibility requires defaultCriteria, we only need to use this hook here to disable the notification
    // after a successful creation or update of a phone tree override, so we will set the criteria to the same
    // values used in other places so that we don't trigger a reset of the eligibility data.
    defaultCriteria: {
      strategy: 'any',
      conditions: [{ type: 'days-shown', maxDaysToShow: 2 }],
    },
  });

  // This is to reset voicmail greeting when voicemail box changes
  useEffect(() => {
    if (isCancelling.current) return;

    if (previousVoicemailBoxId.current && previousVoicemailBoxId.current !== voicemailBoxProps.value) {
      seedValues({
        voicemailMediaId: '',
      });
    }
    previousVoicemailBoxId.current = voicemailBoxProps.value;
  }, [voicemailBoxProps.value]);

  const { create, update } = useOverrideMutations(phoneTenantId, {
    create: {
      onSuccess: (resp) => {
        alerts.success(t('Override created successfully.'));

        if (resp.overrideType === OverrideType.OVERRIDE_TYPE_IVR) {
          // Disable the "New" banners (used in other parts of the app) when a phone tree override is created.
          disableNotification();
        }
      },
      onError: () => {
        alerts.error(t('Failed to create Override.'));
      },
    },
    update: {
      onSuccess: (resp) => {
        alerts.success(t('Override updated successfully.'));
        if (resp.overrideType === OverrideType.OVERRIDE_TYPE_IVR) {
          // Disable the "New" banners (used in other parts of the app) when a phone tree override is updated.
          disableNotification();
        }
      },
      onError: () => {
        alerts.error(t('Failed to update Override.'));
      },
    },
  });

  const createOverrideV2 = (formValues: SchemaFormValues, instructionSetId: string) => {
    const finalState = createPostPayloadV2(formValues, instructionSetId);
    create.mutate(finalState);
  };

  const updateOverrideV2 = (formValues: SchemaFormValues, instructionSetId: string) => {
    const finalState = updatePostPayloadV2(formValues, instructionSetId);
    update.mutate(finalState);
  };

  const [overrideV2, setOverrideV2] = useState<PhoneOverrideType>();

  const handleOpenRemoveModalV2 = (override: PhoneOverrideType) => {
    setOverrideV2(override);
    removeOverride.openModal();
  };

  const media = findMediaItem(vmOverrideMediaProps.value, audioFiles?.allMedia ?? []);
  const resolvedPath = media ? buildAudioLibraryPath({ media }) : '';

  const scheduledCallRoute = [
    {
      id: override?.callRouteId ?? '',
      name: override?.callRouteName ?? '',
    },
  ];

  const overrideOptions = useMemo(
    () =>
      [
        {
          value: OverrideType.OVERRIDE_TYPE_VMO,
          title: t('Send to Voicemail'),
          description: t('All calls are automatically sent to voicemail'),
          trackingId: 'phone-portal-radio-vmo',
          show: true,
        },
        {
          value: OverrideType.OVERRIDE_TYPE_FWD,
          title: t('Forward Call'),
          description: t('All calls are forwarded to a predetermined phone number'),
          trackingId: 'phone-portal-radio-fwd',
          show: true,
        },
        {
          value: OverrideType.OVERRIDE_TYPE_IVR,
          title: (
            <span css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.25) }}>
              {t('Send to Phone Tree')}
              <AnimatedBadge label={t('New')} />
            </span>
          ),
          // Replace title with this when phone tree overrides is generally available
          // title: t('Send to Phone Tree'),
          description: t('All calls are sent to a Phone Tree'),
          trackingId: 'phone-portal-radio-ivr',
          show: hasPhoneTreeOverridesFlag,
        },
      ].filter((option) => option.show),
    [t, hasPhoneTreeOverridesFlag]
  );

  return (
    <>
      <form {...formProps}>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: `calc(100vh - ${useExtraSpacing ? theme.spacing(20) : theme.spacing(8)})`,
          }}
        >
          {!hasUserManagementPermission(override?.locationId ?? '') ? (
            <div>
              <Tray.Header
                Buttons={
                  <Button
                    iconName='x'
                    variant='secondary'
                    size='large'
                    label='close'
                    onClick={() => {
                      onClose?.();
                      // setTimeout for form to reset after closing the modal
                      setTimeout(() => {
                        handleCancel();
                      }, 300);
                    }}
                  />
                }
              >
                <Heading level={2} css={{ marginBottom: 0 }}>
                  {t('View Override')}
                </Heading>
              </Tray.Header>
              <TraySubHeader scheduleCallRoutes={scheduledCallRoute} selectedLocationId={override?.locationId ?? ''} />
              <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
                {t('Override Type')}
              </Heading>
              <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                {['OVERRIDE_TYPE_VMO', 'OVERRIDE_TYPE_FWD'].includes(overrideSelectProps.value) && (
                  <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                    <Text weight='bold'>
                      {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO
                        ? t('Send to Voicemail')
                        : t('Forward Call')}
                    </Text>
                    <Text>
                      {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO
                        ? t('All calls are automatically sent to voicemail')
                        : t('All calls are forwarded to a predetermined phone number')}
                    </Text>
                  </div>
                )}
                <dl
                  css={{
                    display: 'flex',
                    gap: theme.spacing(2),
                    flexDirection: 'column',
                  }}
                >
                  {' '}
                  {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO && (
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                      <Text weight='bold'>{t('Voicemail Box')}</Text>
                      <Text>{voicemailBox?.[0]?.mailbox?.name}</Text>
                    </div>
                  )}
                  <div css={{ display: 'flex', gap: theme.spacing(3) }}>
                    {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO ? (
                      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <Text weight='bold'>{t('Voicemail Greeting')} </Text>
                        {/* {media?.name} */}
                        {/* {voicemailBoxProps.value} */}
                        <Text>{getMediaNameById(vmOverrideMediaProps.value)}</Text>
                      </div>
                    ) : (
                      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <Text weight='bold'>{t('Forwarding Number')}</Text>
                        <Text>
                          {forwardingNumber?.name} ({forwardingNumber?.number})
                        </Text>
                      </div>
                    )}
                    {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO &&
                      getMediaNameById(vmOverrideMediaProps.value) !== 'No Greeting' && (
                        <div css={{ width: '250px' }}>
                          <CachedAudioScrubber
                            filePath={vmOverrideMediaProps.value ? resolvedPath : DEFAULT_GREETING_PATH}
                            mediaId={vmOverrideMediaProps.value ? vmOverrideMediaProps.value : 'default'}
                          />
                        </div>
                      )}
                  </div>
                </dl>
                {optionalFieldProps.value && (
                  <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
                    <Heading level={3}>{t('Start/End Time')}</Heading>
                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                      <div css={{ display: 'flex', gap: theme.spacing(12.5) }}>
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('Start Date')} </Text>
                          <Text>
                            {startDateFieldProps.value
                              ? dayjs(startDateFieldProps.value, 'MM/DD/YYYY').format('M/D/YYYY')
                              : t('N/A')}
                          </Text>
                        </div>
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('Start Time')}</Text>
                          <Text>
                            {startTimeFieldProps.value
                              ? dayjs(startTimeFieldProps.value, 'HH:mm:ss').format('h:mma')
                              : t('N/A')}
                          </Text>
                        </div>
                      </div>
                      <Text color='light' size='small'>
                        {t('Override will automatically start on this date and time')}
                      </Text>
                    </div>

                    <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                      <div css={{ display: 'flex', gap: theme.spacing(12.5) }}>
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('End Date')} </Text>
                          <Text>
                            {endDateFieldProps.value
                              ? dayjs(endDateFieldProps.value, 'MM/DD/YYYY').format('M/D/YYYY')
                              : t('N/A')}
                          </Text>
                        </div>
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                          <Text weight='bold'>{t('End Time')}</Text>
                          <Text>
                            {endTimeFieldProps.value
                              ? dayjs(endTimeFieldProps.value, 'HH:mm:ss').format('h:mma')
                              : t('N/A')}
                          </Text>
                        </div>
                      </div>
                      <Text color='light' size='small'>
                        {t('Override will automatically stop on this date and time')}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <SurfaceHeader.Root
                noBorder
                css={{ margin: theme.spacing(-4, -4, 0, -4), width: `calc(100% + ${theme.spacing(8)})` }}
              >
                <SurfaceHeader.Row>
                  <SurfaceHeader.Title>{isEdit ? t('Edit Override') : t('Create Override')}</SurfaceHeader.Title>
                  <SurfaceHeader.Actions>
                    <SurfaceHeader.Icon
                      iconName='x'
                      onClick={() => {
                        onClose?.();
                        // setTimeout for form to reset after closing the modal
                        setTimeout(() => {
                          handleCancel();
                        }, 300);
                      }}
                    />
                  </SurfaceHeader.Actions>
                </SurfaceHeader.Row>
                <SurfaceHeader.Body>
                  <TraySubHeader
                    scheduleCallRoutes={scheduledCallRoute}
                    selectedLocationId={override?.locationId ?? ''}
                    routeType={isDeviceRouteOverride ? 'deviceRoute' : 'callRoute'}
                  />
                </SurfaceHeader.Body>
              </SurfaceHeader.Root>
              <Text size='medium' css={{ marginBottom: theme.spacing(2) }}>
                {t(
                  'Choose the override type and, if you wish to plan ahead, select the start and end dates for the override.'
                )}
              </Text>

              {!!voicemailIvr && hasPhoneTreeOverridesFlag && (
                <VoicemailIvrNotification
                  ivrMenuId={voicemailIvr.ivrMenuId}
                  ivrMenuName={voicemailIvr.ivrMenuName}
                  audioFiles={audioFiles}
                />
              )}

              <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
                {t('Select Override Type')}
              </Heading>
              <div css={overrideSettingsStyle}>
                <RadioCardField
                  {...overrideSelectProps}
                  css={css`
                    > div:first-of-type {
                      display: flex;
                      flex-wrap: wrap;
                      gap: ${theme.spacing(2)};
                    }
                  `}
                >
                  {overrideOptions.map((option) => (
                    <RadioCardField.Option
                      key={option.value}
                      value={option.value}
                      trackingId={option.trackingId}
                      css={css`
                        flex: 0 0 240px;
                      `}
                      title={option.title}
                      description={option.description}
                    />
                  ))}
                </RadioCardField>

                {/* Dropdown fields for Voicemail override */}
                {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_VMO && (
                  <div>
                    <DropdownField
                      {...voicemailBoxProps}
                      css={{ marginBottom: theme.spacing(3) }}
                      label={t('Select Voicemail Box')}
                      name='voicemailBoxId'
                    >
                      {!!lastUsedVoicemailBoxId && (
                        <DropdownField.OptionGroup label={t('Most Recently Used')}>
                          <DropdownField.Option
                            key={lastUsedVoicemailBoxId}
                            value={lastUsedVoicemailBoxId}
                            css={{ '&:hover': { backgroundColor: theme.colors.neutral5 } }}
                          >
                            {getVoicemailBoxNameById(lastUsedVoicemailBoxId)}
                          </DropdownField.Option>
                        </DropdownField.OptionGroup>
                      )}
                      {
                        <DropdownField.OptionGroup label={t('Voicemail Boxes')}>
                          {filteredVoicemailBoxes?.map((option) => (
                            <DropdownField.Option key={option.mailbox.id} value={option.mailbox.id}>
                              {option.mailbox.name}
                            </DropdownField.Option>
                          ))}
                        </DropdownField.OptionGroup>
                      }
                    </DropdownField>

                    <div
                      css={css`
                        .audio-picker {
                          display: flex;
                          flex-direction: column;
                          gap: ${theme.spacing(0)};

                          > div:first-of-type {
                            min-width: 100%;
                          }

                          > div:last-of-type {
                            max-width: 240px;
                          }
                        }
                      `}
                    >
                      <AudioPicker
                        allowedOptions={{
                          add: true,
                          custom: true,
                          default: true,
                          recent: true,
                          none: true,
                          mailbox: true,
                        }}
                        field={vmOverrideMediaProps}
                        name='voicemailMediaId'
                        labels={{ field: t('Select Voicemail Greeting') }}
                        tenantId={phoneTenantId}
                        lastUsedMediaItemId={lastUsedMediaItemId}
                        mailboxId={voicemailBoxProps.value}
                      />
                    </div>
                  </div>
                )}

                {/* Dropdown field for Call Forwarding override */}
                {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_FWD && (
                  <ForwardingNumberPicker
                    {...callForwardingNumberProps}
                    css={css`
                      width: 100%;
                    `}
                    name='forwardingNumberId'
                    label={t('Select Forwarding Number')}
                    locationId={locationId}
                    lastUsedForwardingNumberId={lastUsedForwardingNumberId}
                  />
                )}

                {/* Dropdown fields for Phone Tree override */}
                {overrideSelectProps.value === OverrideType.OVERRIDE_TYPE_IVR && (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: ${theme.spacing(2)};
                    `}
                  >
                    <div
                      css={css`
                        .audio-picker {
                          display: flex;
                          flex-direction: column;
                          gap: ${theme.spacing(0)};

                          > div:first-of-type {
                            min-width: 100%;
                          }

                          > div:last-of-type {
                            max-width: 240px;
                          }
                        }
                      `}
                    >
                      <AudioPicker
                        allowedOptions={{
                          add: true,
                          custom: true,
                          default: false,
                          recent: true,
                          none: true,
                          mailbox: false,
                        }}
                        trackingIds={{ audioScrubber: 'nwx-override-tray-phone-tree-info-message' }}
                        field={phoneTreeInfoMediaIdProps}
                        name='phoneTreeInfoMediaId'
                        labels={{
                          field: t('Select Informational Message (Optional)'),
                          none: t('No Audio File (Default)'),
                        }}
                        tenantId={phoneTenantId}
                        helperText={t(
                          'An informational message that explains the reason for the Override before the Phone Tree prompt plays.'
                        )}
                        lastUsedMediaItemId={lastUsedIVRMediaId}
                      />
                    </div>

                    <div
                      css={css`
                        display: flex;
                        gap: ${theme.spacing(2)};
                        flex-direction: column;
                      `}
                    >
                      {hasUnsavedPhoneTrees && (
                        <InlineNotification
                          status='info'
                          size='medium'
                          css={css`
                            > div {
                              width: 100%;
                            }
                          `}
                        >
                          <Trans t={t}>
                            Unsaved Phone Trees won’t be available until you save this Call Route.{' '}
                            <TextLink
                              css={css`
                                cursor: pointer;
                              `}
                              onClick={handleSaveCallRoute}
                              trackingId='nwx-overrides-tray-unsaved-phone-trees-notification-save-call-route-link'
                            >
                              {'Save Call Route'}
                            </TextLink>
                          </Trans>
                        </InlineNotification>
                      )}

                      <div
                        css={css`
                          display: flex;
                          gap: ${theme.spacing(1)};
                          flex-direction: column;
                        `}
                      >
                        <PhoneTreeDropdown
                          dropdownProps={phoneTreeIdProps}
                          name='phoneTreeId'
                          phoneTreesInCallRoute={filteredPhoneTreesInCallRoute}
                          currentCallRouteId={callRouteId}
                          currentCallRouteName={override?.callRouteName}
                          currentCallRouteLocationId={override?.locationId}
                          prioritizedLocationId={iLID}
                          lastUsedPhoneTreeId={lastUsedIVRId}
                          disableUnsavedPhoneTrees={true}
                          locationPhoneTenantId={phoneTenantId}
                          css={css`
                            width: 100%;
                          `}
                        />
                        {!!phoneTreeIdProps.value && (
                          <Button
                            variant='tertiary'
                            iconName='view'
                            css={css`
                              width: fit-content;
                            `}
                            {...previewPhoneTreeTriggerProps}
                            trackingId='nwx-override-tray-preview-phone-tree-btn'
                          >
                            {t('Preview Phone Tree')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {!!phoneTreeData?.promptMediaItemId && (
                      <PhoneTreePromptSection
                        mediaItemId={phoneTreeData.promptMediaItemId}
                        audioFiles={
                          audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }
                        }
                      />
                    )}

                    <Modal {...previewPhoneTreeModalProps} minWidth={800} maxWidth={800}>
                      <PhoneTreePreviewModal
                        phoneTreeId={phoneTreeIdProps.value ?? ''}
                        name={phoneTreeData?.name ?? ''}
                        onClose={previewPhoneTreeModalProps.onClose}
                        callRouteName={phoneTreeData?.callRouteData?.name}
                        callRouteLocationId={phoneTreeData?.callRouteData?.locationId}
                        audioFiles={
                          audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }
                        }
                      />
                    </Modal>
                  </div>
                )}

                <CheckboxField
                  {...optionalFieldProps}
                  label={
                    <>
                      <Text>{t('Add Start/End Time (Optional)')}</Text>
                      <Text size='medium' color='light'>
                        {t('Schedule when your override starts, ends, or both.')}
                      </Text>
                    </>
                  }
                  data-trackingid='phone-portal-checkBox-addStartEndTime'
                />

                {optionalFieldProps.value && (
                  <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
                    <div>
                      <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                        <DatePickerField
                          label={t('Start Date')}
                          {...startDateFieldProps}
                          css={{ width: '155px' }}
                          minDate={currentDateFormatted}
                          data-trackingid='phone-portal-dateField-startDate'
                        />
                        <TimeField
                          {...startTimeFieldProps}
                          showAdornment
                          label={t('Start Time')}
                          interval={15}
                          minTime={isStartToday ? currentTimeOffice : '0:00'}
                          maxTime='24:00'
                          css={{ width: '155px' }}
                          value={startTimeFieldProps.value ? startTimeFieldProps.value : ''}
                          data-trackingid='phone-portal-timeField-startTime'
                        />
                      </div>
                      <Text color='light' size='small'>
                        {t('Override will automatically start on this date and time')}
                      </Text>
                    </div>
                    <div>
                      <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                        <DatePickerField
                          label={t('End Date')}
                          {...endDateFieldProps}
                          css={{ width: '155px' }}
                          minDate={startDate ? startDate : currentDateFormatted}
                          data-trackingid='phone-portal-dateField-endDate'
                        />
                        <TimeField
                          {...endTimeFieldProps}
                          showAdornment
                          label={t('End Time')}
                          interval={15}
                          minTime={isEndToday ? currentTimeOffice : '0:00'}
                          maxTime='24:00'
                          css={{ width: '155px' }}
                          value={endTimeFieldProps.value ? endTimeFieldProps.value : ''}
                          data-trackingid='phone-portal-timeField-endTime'
                        />
                      </div>
                      <Text color='light' size='small'>
                        {t('Override will automatically stop on this date and time')}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasUserManagementPermission(override?.locationId ?? '') && (
            <section css={newOverrideButtonSectionStyle}>
              <>
                <div>
                  {isEdit && (
                    <TextLink
                      weight='bold'
                      color='error'
                      disabled={isLoading}
                      css={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                      }}
                      onClick={() => {
                        handleOpenRemoveModalV2(override);
                      }}
                      size='large'
                    >
                      <Icon name='remove2-small' />
                      {t('Remove Override')}
                    </TextLink>
                  )}
                </div>
                <div css={{ display: 'flex', gap: theme.spacing(2) }}>
                  <Button
                    size='large'
                    variant='secondary'
                    disabled={isLoading}
                    css={{ width: 'auto' }}
                    onClick={() => {
                      onClose?.();
                      setTimeout(() => {
                        // setTimeout for form to reset after closing the modal
                        handleCancel();
                      }, 300);
                    }}
                    trackingId='phone-portal-button-cancelOverride'
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    size='large'
                    css={{ width: 'auto' }}
                    disabled={!canUpdateOverride || needTimeField || isLoading}
                    onClick={() => onClose?.()}
                    type='submit'
                    trackingId={
                      isDeviceRouteOverride
                        ? 'overrides-deviceExtensionRoute-createEdit-save-btn'
                        : 'phone-portal-button-createEditOverride'
                    }
                  >
                    {t('Save')}
                  </Button>
                </div>
              </>
              <NewOverrideSettingRemoveModal
                override={overrideV2 ?? ({} as PhoneOverrideType)}
                modalProps={removeOverride.modalProps}
                phoneTenantId={phoneTenantId}
                onSuccess={onClose}
              />
            </section>
          )}
        </div>
      </form>
    </>
  );
};

const PhoneTreePromptSection = ({ mediaItemId, audioFiles }: { mediaItemId: string; audioFiles: AudioQueryMedia }) => {
  const { t } = useTranslation('phone');

  if (!mediaItemId) {
    return null;
  }

  let filePath = '';
  const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
  if (media) {
    filePath = buildAudioLibraryPath({ media });
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(0.5)};
        max-width: 240px;
        position: relative;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing(0.5)};
        `}
      >
        <Text size='small' color='subdued'>
          {t('Prompt:')}
        </Text>
        <Truncated as={Text} size='small' color='subdued'>
          {getNameWithoutExtension(media?.name)}
        </Truncated>
      </div>
      {!!filePath && (
        <CachedAudioScrubber
          singlePlayer
          mediaId={mediaItemId}
          filePath={filePath}
          trackingId='nwx-override-tray-phone-tree-prompt'
        />
      )}
    </div>
  );
};
